import {
  AlertLooks,
  BadgePill,
  BrandfolderHexColors,
  CloseButton,
  FontIcon,
  FontIcons,
  StandardAlert,
  StandardLink,
  StandardTooltip,
  Placements,
} from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import classNames from 'classnames';
import React, { FunctionComponent, ReactNode, useState } from 'react';

import { useDialog } from '@components/common/custom_hooks/useDialog';
import { TextButton } from '@components/library/button';

import { CombinedRoleLimitsTable } from '@components/limits/CombinedRoleLimitsTable';
import { FolderLimitsTable } from '@components/limits/FolderLimitsTable';
import { useCustomRole } from '@api/v4/organizations/custom_permissions';
import { defaultKnowledgeBaseLinks, gettyKnowledgeBaseLinks } from '@components/kb-article-links';
import { isGettyClient } from '@helpers/getty-strings';

import { planLimitsDialogOptions } from '../helpers/limitsDialogOptions';

import { formatToProperCaseType } from './helpers';
import './usage_info_card.scss';
import { InfoCard } from './UsageInfoCard';

enum LimitTypeTitles {
  Administrators = 'admin',
  Collaborators = 'collaborator',
  Storage = 'storage',
  'Combined roles' = 'combined_role'
}

export interface UsageInfoCardContentProps {
  card: InfoCard;
}

const UsageInfoCardContent: FunctionComponent<InfoCard> = ({
  editEnabled = false,
  icon,
  title,
  usage,
  dangerAlert,
  hasSetLimits = false,
  isEnabled,
  link,
  orgKey = BFG.organization_key,
  setLimitsAction,
  subtitle,
  warningAlert,
  openEditDialog,
  newRoleTooltip,
  editCapabilitiesTooltip,
}) => {
  const { closeDialog, openDialog } = useDialog();
  const [newRoleTooltipOpen, setNewRoleTooltipOpen] = useState<boolean>(!!newRoleTooltip?.open);
  const [editCapabilitiesTooltipOpen, setEditCapabilitiesTooltipOpen] = useState<boolean>(!!editCapabilitiesTooltip?.open);

  const infoSectionClasses = classNames({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'usageInfoCard__info': true,
    hasSubtitle: !!subtitle
  });

  const titleCasedTitle = formatToProperCaseType(title);
  const upgradeText = t`Upgrade for ${titleCasedTitle}`;
  const upgradeEmailBody = `I\'d like to inquire about adding more ${title}.`;
  const upgradeEmailLink = `mailto:${BFG.supportEmail}?subject=Upgrade Inquiry&body=${upgradeEmailBody}`;

  const combinedRoleLimitsFeatureOn = BFG.hasFeature('combined_role_limits_rollout');
  const limitsFeatureOn = BFG.hasFeature('resource_level_limit');

  const limitIsUnlimited = usage.limit === 'Unlimited' || usage.limit === -1;
  const usageLimit = !!usage.limit ? usage.limit : 0;

  const dangerMessage = dangerAlert && !limitIsUnlimited && (
    <StandardAlert
      icon={FontIcons.Warning}
      look={AlertLooks.Error}
      showHeading={false}
    >
      {dangerAlert}
    </StandardAlert>
  );

  const warningMessage = warningAlert && !dangerAlert && !limitIsUnlimited && (
    <StandardAlert look={AlertLooks.Warning} showHeading={false}>
      {warningAlert}
    </StandardAlert>
  );

  const disabledMessage = !isEnabled && (
    <BadgePill
      backgroundColor={BrandfolderHexColors.Gray}
      className="usageInfoCard__disabled-notification"
      fontSize={16}
    >
      <Trans>Not enabled</Trans>
    </BadgePill>
  );

  const handleCloseDialog = (): void => {
    closeDialog();
    if (setLimitsAction) {
      setLimitsAction();
    }
  };
  const openSetLimitsDialog = (): void => {
    if (combinedRoleLimitsFeatureOn) {
      // When combined_role_limits_rollout is enabled, use the combined table for both admin and collaborator
      openDialog(
        <CombinedRoleLimitsTable orgKey={orgKey} setOpen={handleCloseDialog} />,
        planLimitsDialogOptions('combined_role')
      );
    } else {
      // Otherwise use the regular FolderLimitsTable
      openDialog(
        <FolderLimitsTable limitType={LimitTypeTitles[title]} orgKey={orgKey} setOpen={handleCloseDialog} />,
        planLimitsDialogOptions(LimitTypeTitles[title])
      );
    }
  };

  const usageDisplay = (): ReactNode => {
    if (!isEnabled) {
      return (
        <strong>0</strong>
      );
    }

    if (usage.limit === undefined) {
      return (
        <strong>{usage.current}</strong>
      );
    }

    if (limitIsUnlimited) {
      return (
        <Trans>
          <strong>{usage.current}</strong>&nbsp;
          {!combinedRoleLimitsFeatureOn || limitsFeatureOn ? <span>of Unlimited</span> : null}
        </Trans>
      );
    }

    return (
      <Trans>
        <strong>{usage.current}</strong>&nbsp;
        {!combinedRoleLimitsFeatureOn || limitsFeatureOn ? <span>of {usageLimit}</span> : null}
      </Trans>
    );
  };

  // Tooltip content next to the heading
  const { customRole } = useCustomRole(orgKey);

  const knowledgeBaseUrl = isGettyClient()
    ? gettyKnowledgeBaseLinks.userPermissionsLink
    : defaultKnowledgeBaseLinks.userPermissionsLink;

  const hasTooltip = title === 'Administrators' || title === 'Collaborators' || title === customRole?.label;

  const renderRoleTooltip = (): ReactNode => {
    if (title === 'Administrators') {
      return (
        <>
          <span>
            <Trans>
              Admins have full access to customize users, edit assets, and add/remove users at all specified levels.
            </Trans>
          </span>
          <StandardLink href={knowledgeBaseUrl} target="_blank" style={{ color: '#fff', display: 'block', marginTop: 0, textDecoration: 'underline', fontSize: '13px' }}>
            <Trans>Learn more about user permissions</Trans>
          </StandardLink>
        </>
      );
    }

    if (title === 'Collaborators') {
      return (
        <>
          <span>
            <Trans>Collaborators can add/edit assets within a specified level.</Trans>
          </span>
          <StandardLink href={knowledgeBaseUrl} target="_blank" style={{ color: '#fff', display: 'block', marginTop: 0, textDecoration: 'underline', fontSize: '13px' }}>
            <Trans>Learn more about user permissions</Trans>
          </StandardLink>
        </>
      );
    } else if (title === customRole?.label) {
      // Custom role tooltip (fallback if neither Admin nor Collaborator)
      const capabilityLabels: string[] = [];
      if (customRole?.asset_approve) capabilityLabels.push(t`Approve assets`);
      if (customRole?.add_to_shares) capabilityLabels.push(t`Manage share links`);
      if (customRole?.view_asset_usage) capabilityLabels.push(t`View asset usage`);
      if (customRole?.edit_brandguide) capabilityLabels.push(t`Edit Brandguide`);

      return (
        <>
          <span>
            <Trans>
              {customRole?.label} have all capabilities of Collaborators{capabilityLabels.length ? ', plus the ability to:' : '.'}
            </Trans>
          </span>
          {capabilityLabels.length > 0 && (
            <ul style={{ marginLeft: '16px', marginTop: '4px', color: '#fff' }}>
              {capabilityLabels.map((text) => (
                <li key={text} style={{ color: '#fff', fontSize: '13px', lineHeight: '20px' }}>{text}</li>
              ))}
            </ul>
          )}
          <StandardLink href={knowledgeBaseUrl} target="_blank" style={{ color: '#fff', display: 'block', marginTop: 0, textDecoration: 'underline', fontSize: '13px' }}>
            <Trans>Learn more about user permissions</Trans>
          </StandardLink>
        </>
      );
    }

    return null;
  };

  return (
    <>
      <div className="usageInfoCard__top">
        <FontIcon
          aria-hidden
          icon={FontIcons[icon]}
          iconSize={20}
          title={FontIcons[icon]}
        />
        {dangerMessage}
        {warningMessage}
        {disabledMessage}
      </div>
      <div className={infoSectionClasses}>
        {usageDisplay()}
        <h3 className="truncated-title">
          <span className="title-text" title={title}>
            {title}
          </span>
          {hasTooltip ? (
            <StandardTooltip
              id={`usage-info-card-${title}-tooltip`}
              placement={Placements.RightCenter}
              triggerOffset={6}
              tooltip={renderRoleTooltip()}
            >
              <FontIcon
                aria-hidden
                icon={FontIcons.Info}
                iconSize={14}
                title={t`More info`}
                style={{ marginLeft: 6 }}
              />
            </StandardTooltip>
          ) : <span />}
        </h3>
        {subtitle && <span>{subtitle}</span>}
      </div>
      <div className="usageInfoCard__links">
        {link && isEnabled && <a href={link.linkTo}>{link.name}</a>}
        {!isEnabled && (
          <a href={upgradeEmailLink} target="_blank">
            {upgradeText}
          </a>
        )}
        {hasSetLimits && isEnabled && (
          <>
            {newRoleTooltip?.open ? (
              <StandardTooltip
                defaultOpen={newRoleTooltipOpen}
                id={`new-role-tooltip-${title}`}
                openOnHover={false}
                placement={Placements.BottomStart}
                triggerOffset={12}
                tooltip={(() => {
                  const body = (
                    <div style={{ maxWidth: 360 }}>
                      <strong style={{ marginBottom: 8, display: 'block' }}><Trans>New role has been created!</Trans></strong>
                      <Trans>
                        Set role limits for each resource, add up to {newRoleTooltip?.maxTotal ?? '0'} roles total across all Organizations and Brandfolders from here.
                      </Trans>
                    </div>
                  );
                  return (
                    <div style={{ position: 'relative', paddingRight: 18 }}>
                      {body}
                      <CloseButton
                        aria-label={t`Close`}
                        className="tooltip-close-x"
                        onClick={() => {
                          setNewRoleTooltipOpen(false)
                          newRoleTooltip.onClose();
                        }}
                        style={{ position: 'absolute', top: 0, right: 0, color: '#fff', fontSize: '16px' }}
                      />
                    </div>
                  );
                })()}
                width={undefined}
              >
                <TextButton
                  aria-label="Set limits"
                  onClick={openSetLimitsDialog}
                  onKeyDown={openSetLimitsDialog}
                >
                  <Trans>Set limits</Trans>
                </TextButton>
              </StandardTooltip>
            ) : (
              <TextButton
                aria-label="Set limits"
                onClick={openSetLimitsDialog}
                onKeyDown={openSetLimitsDialog}
              >
                <Trans>Set limits</Trans>
              </TextButton>
            )}
          </>
        )}
        {editEnabled && isEnabled && (
          <>
            {!hasSetLimits && editCapabilitiesTooltip?.open ? (
              <StandardTooltip
                defaultOpen={editCapabilitiesTooltipOpen}
                id={`edit-capabilities-tooltip-${title}`}
                openOnHover={false}
                placement={Placements.BottomStart}
                triggerOffset={12}
                tooltip={(() => {
                  const body = (
                    <div style={{ maxWidth: 360 }}>
                      <strong style={{ marginBottom: 8, display: 'block' }}><Trans>New role has been created!</Trans></strong>
                      <Trans>
                        {editCapabilitiesTooltip?.message || t`Invite people from your organization and assign them to this new role.`}
                      </Trans>
                    </div>
                  );
                  return (
                    <div style={{ position: 'relative', paddingRight: 18 }}>
                      {body}
                      <CloseButton
                        aria-label={t`Close`}
                        className="tooltip-close-x"
                        onClick={() => {
                          setEditCapabilitiesTooltipOpen(false);
                          editCapabilitiesTooltip.onClose();
                        }}
                        style={{ position: 'absolute', top: 0, right: 0, color: '#fff', fontSize: '16px' }}
                      />
                    </div>
                  );
                })()}
                width={undefined}
              >
                <TextButton
                  aria-label="Edit"
                  onClick={openEditDialog}
                  onKeyDown={openEditDialog}
                >
                  <Trans>Edit role</Trans>
                </TextButton>
              </StandardTooltip>
            ) : (
              <TextButton
                aria-label="Edit"
                onClick={openEditDialog}
                onKeyDown={openEditDialog}
              >
                <Trans>Edit role</Trans>
              </TextButton>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UsageInfoCardContent;
